/**
 * Production Logger - Monitoring and Security Event Logging
 *
 * Provides centralized logging for security events, performance metrics,
 * and operational monitoring in production environments.
 */

class ProductionLogger {
  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.logs = [];
    this.maxLogs = 1000; // Keep last 1000 logs in memory
  }

  /**
   * Log security events for audit trails
   */
  logSecurityEvent(eventType, data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'security',
      event: eventType,
      data: this.sanitizeData(data),
      sessionId: this.getSessionId()
    };

    this.addLog(logEntry);

    // In development, log to console
    if (this.isDevelopment) {
      console.log(`🔒 Security Event: ${eventType}`, logEntry);
    }

    // In production, you would send this to your logging service
    // Example: this.sendToLoggingService(logEntry);
  }

  /**
   * Log performance metrics
   */
  logPerformanceMetric(metricName, value, metadata = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'performance',
      metric: metricName,
      value,
      metadata: this.sanitizeData(metadata),
      sessionId: this.getSessionId()
    };

    this.addLog(logEntry);

    if (this.isDevelopment) {
      console.log(`⚡ Performance: ${metricName}`, logEntry);
    }
  }

  /**
   * Log operational events
   */
  logOperationalEvent(eventType, data) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'operational',
      event: eventType,
      data: this.sanitizeData(data),
      sessionId: this.getSessionId()
    };

    this.addLog(logEntry);

    if (this.isDevelopment) {
      console.log(`🔧 Operational: ${eventType}`, logEntry);
    }
  }

  /**
   * Log error events
   */
  logError(error, context = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      type: 'error',
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name
      },
      context: this.sanitizeData(context),
      sessionId: this.getSessionId()
    };

    this.addLog(logEntry);

    if (this.isDevelopment) {
      console.error(`❌ Error:`, logEntry);
    }
  }

  /**
   * Add log to memory buffer
   */
  addLog(logEntry) {
    this.logs.push(logEntry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Sanitize sensitive data from logs
   */
  sanitizeData(data) {
    if (!data || typeof data !== 'object') {
      return data;
    }

    const sanitized = { ...data };
    const sensitiveFields = ['password', 'token', 'secret', 'key', 'auth'];

    Object.keys(sanitized).forEach(key => {
      const lowerKey = key.toLowerCase();
      if (sensitiveFields.some(field => lowerKey.includes(field))) {
        sanitized[key] = '[REDACTED]';
      }
    });

    return sanitized;
  }

  /**
   * Get or create session ID for tracking
   */
  getSessionId() {
    if (!this.sessionId) {
      this.sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    return this.sessionId;
  }

  /**
   * Get recent logs (for debugging)
   */
  getRecentLogs(count = 50) {
    return this.logs.slice(-count);
  }

  /**
   * Get logs by type
   */
  getLogsByType(type) {
    return this.logs.filter(log => log.type === type);
  }

  /**
   * Clear logs (for testing)
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Get logging statistics
   */
  getStats() {
    const typeStats = {};
    this.logs.forEach(log => {
      typeStats[log.type] = (typeStats[log.type] || 0) + 1;
    });

    return {
      totalLogs: this.logs.length,
      typeBreakdown: typeStats,
      sessionId: this.sessionId,
      oldestLog: this.logs.length > 0 ? this.logs[0].timestamp : null,
      newestLog: this.logs.length > 0 ? this.logs[this.logs.length - 1].timestamp : null
    };
  }

  // Future: Send to external logging service
  // sendToLoggingService(logEntry) {
  //   // Implementation for sending to external service
  //   // e.g., DataDog, LogRocket, Sentry, etc.
  // }
}

// Create singleton instance
const productionLogger = new ProductionLogger();

// Expose to window in development for debugging
if (process.env.NODE_ENV === 'development') {
  window.productionLogger = productionLogger;
  console.log('🔧 Production Logger available at window.productionLogger');
}

export default productionLogger;